import { useEffect, useState } from 'react'
import { Box, Button, CloseButton, Drawer, HStack, Portal, Text, VStack } from '@chakra-ui/react'
import type { WorksiteInfoDto } from '../../../types/canvasser'
import { readScriptLanguage, writeScriptLanguage, type ScriptLanguage } from '../../../lib/walkPrefs'

/**
 * [O3] The canvasser's "Worksite info" bottom sheet: the worksite name, address, manager and a short
 * description. Reachable from the dock and from "Before you go in". `info` is null on home stops, when
 * everything is blank, and on a pre-O3 cache — the sheet then shows just the name and address.
 * Phone and email are never part of the payload.
 */
export function WorksiteInfoSheet({ open, onClose, workplaceLabel, address, info }: {
  open: boolean; onClose: () => void; workplaceLabel: string | null; address: string; info: WorksiteInfoDto | null
}) {
  const [lang, setLang] = useState<ScriptLanguage>(readScriptLanguage)
  // The sheet stays mounted under "Before you go in", whose language toggle writes the same pref:
  // re-read it each time the sheet opens so a toggle made there shows here.
  useEffect(() => { if (open) setLang(readScriptLanguage()) }, [open])
  const description = lang === 'es' && info?.descriptionEs ? info.descriptionEs : info?.description ?? info?.descriptionEs ?? null
  const toggle = () => {
    const next = lang === 'en' ? 'es' : 'en'
    setLang(next)
    writeScriptLanguage(next)
  }
  return (
    <Drawer.Root open={open} placement="bottom" onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" pb="env(safe-area-inset-bottom, 0px)" shadow="popover">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
            <Drawer.Header><Drawer.Title>Worksite info</Drawer.Title></Drawer.Header>
            <Drawer.Body>
              <VStack align="stretch" gap={3} pb={4}>
                <HStack justify="space-between" align="flex-start" gap={2}>
                  <VStack align="stretch" gap={1} minW={0}>
                    <Text fontWeight="bold" fontSize="lg">{workplaceLabel ?? 'Worksite'}</Text>
                    <Text color="fg.muted">{address}</Text>
                  </VStack>
                  {info?.description && info?.descriptionEs && (
                    <Button size="sm" minW="44px" minH="44px" variant="outline" borderColor="border" onClick={toggle}>
                      {lang === 'en' ? 'ES' : 'EN'}
                    </Button>
                  )}
                </HStack>
                {info?.manager && <Text fontWeight="semibold">{`Manager: ${info.manager}`}</Text>}
                {description && <Box p={3} borderRadius="md" bg="bg.subtle"><Text>{description}</Text></Box>}
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
